"use client";

import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { IUser, IRole, ProfileData, UserPreferences } from "../types";
import { setSsoCookies, clearSsoCookies, getSsoCookies, resumeFromCookies } from "../ssoClient";

export const STORAGE_KEY_PREFIX = "ai-dashboard";

interface AuthContextType {
    user: IUser | null;
    accessToken: string | null;
    mapToken: string | null;
    profileData: ProfileData | null;
    userPreferences: UserPreferences | null;
    permissions: Set<string>;
    isLoading: boolean;
    isAuthenticated: boolean;
    hydrated: boolean;
    setProfileData: (profileData: ProfileData) => void;
    setUserPreferences: (userPreferences: UserPreferences) => void;
    signIn: (accessToken: string, user: IUser, mapToken: string, profileData: ProfileData, userPreferences: UserPreferences) => void;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);
AuthContext.displayName = "AuthContext";

interface AuthProviderProps {
    children: ReactNode;
}

function extractPermissions(user: IUser): Set<string> {
    const perms = new Set<string>();
    user.roles?.forEach((role: IRole) => {
        role.permissions?.forEach((p) => perms.add(p.name));
    });
    return perms;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [mapToken, setMapToken] = useState<string | null>(null);
    const [permissions, setPermissions] = useState<Set<string>>(new Set());
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [hydrated, setHydrated] = useState(false);

    const clearSession = useCallback((redirectTo?: string) => {
        setUser(null);
        setAccessToken(null);
        setMapToken(null);
        setPermissions(new Set());
        setProfileData(null);
        setUserPreferences(null);

        localStorage.removeItem(`${STORAGE_KEY_PREFIX}.authData`);
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}.accessToken`);
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}.mapToken`);
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}.profileData`);
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}.userPreferences`);

        localStorage.removeItem("selectedDealer");
        localStorage.removeItem("selectedDateRange");
        localStorage.removeItem("selectedCompareEnabled");
        localStorage.removeItem("selectedCompareDateRange");
        sessionStorage.removeItem("selectedDealer");
        sessionStorage.removeItem("selectedDateRange");
        sessionStorage.removeItem("selectedCompareEnabled");
        sessionStorage.removeItem("selectedCompareDateRange");

        if (redirectTo && window.location.pathname !== redirectTo) {
            window.location.href = redirectTo;
        }
    }, []);

    const signIn = useCallback((newAccessToken: string, newUser: IUser, newMapToken: string, newProfileData: ProfileData, newUserPreferences: UserPreferences) => {
        setUser(newUser);
        setAccessToken(newAccessToken);
        setMapToken(newMapToken);
        setPermissions(extractPermissions(newUser));
        setProfileData(newProfileData);
        setUserPreferences(newUserPreferences);

        localStorage.setItem(`${STORAGE_KEY_PREFIX}.authData`, JSON.stringify(newUser));
        localStorage.setItem(`${STORAGE_KEY_PREFIX}.accessToken`, newAccessToken);
        localStorage.setItem(`${STORAGE_KEY_PREFIX}.mapToken`, newMapToken);
        localStorage.setItem(`${STORAGE_KEY_PREFIX}.profileData`, JSON.stringify(newProfileData));
        localStorage.setItem(`${STORAGE_KEY_PREFIX}.userPreferences`, JSON.stringify(newUserPreferences));

        setSsoCookies(newMapToken, newUser.userId);
    }, []);

    const signOut = useCallback(() => {
        console.log("[SIGNOUT] Logging out...");
        clearSsoCookies();
        clearSession("/");
    }, [clearSession]);

    // Initialize from localStorage on mount, with SSO cookie fallback.
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const storedUser = localStorage.getItem(`${STORAGE_KEY_PREFIX}.authData`);
                const storedProfileData = localStorage.getItem(`${STORAGE_KEY_PREFIX}.profileData`);
                const storedToken = localStorage.getItem(`${STORAGE_KEY_PREFIX}.accessToken`);
                const storedMapToken = localStorage.getItem(`${STORAGE_KEY_PREFIX}.mapToken`);
                const storedUserPreferences = localStorage.getItem(`${STORAGE_KEY_PREFIX}.userPreferences`);
                const hasStoredSession = storedUser && storedToken && storedMapToken && storedProfileData && storedUserPreferences;
                const ssoCookies = getSsoCookies();

                if (hasStoredSession) {
                    if (!ssoCookies) {
                        clearSession("/");
                        return;
                    }

                    const parsedUser = JSON.parse(storedUser) as IUser;
                    const parsedProfileData = JSON.parse(storedProfileData) as ProfileData;
                    const parsedUserPreferences = JSON.parse(storedUserPreferences) as UserPreferences;
                    setUser(parsedUser);
                    setAccessToken(storedToken);
                    setMapToken(storedMapToken);
                    setPermissions(extractPermissions(parsedUser));
                    setProfileData(parsedProfileData);
                    setUserPreferences(parsedUserPreferences);
                } else {
                    // No local session: attempt SSO resume via shared cookie from the other app.
                    const session = await resumeFromCookies();
                    if (session) {
                        signIn(session.accessToken, {
                            userId: session.authData.userId,
                            userName: session.authData.userName ?? "",
                            roles: session.authData.roles,
                        }, session.mapToken, session.profileData, session.userPreferences as UserPreferences);
                    }
                }
            } catch (error) {
                console.error("Failed to initialize auth:", error);
                clearSession();
            } finally {
                setIsLoading(false);
                setHydrated(true);
            }
        };

        initializeAuth();

        // Cross-tab logout: when another tab clears the token, sync this tab.
        const handleStorage = (e: StorageEvent) => {
            if (e.key === `${STORAGE_KEY_PREFIX}.accessToken` && !e.newValue) {
                clearSession("/");
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [clearSession, signIn]);

    useEffect(() => {
        if (!hydrated || !accessToken) return;

        const syncLogoutFromSsoCookies = () => {
            if (!getSsoCookies()) {
                clearSession("/");
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                syncLogoutFromSsoCookies();
            }
        };

        window.addEventListener("focus", syncLogoutFromSsoCookies);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        const intervalId = window.setInterval(syncLogoutFromSsoCookies, 5000);

        return () => {
            window.removeEventListener("focus", syncLogoutFromSsoCookies);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.clearInterval(intervalId);
        };
    }, [hydrated, accessToken, clearSession]);

    return (
        <AuthContext.Provider
            value={{
                user,
                profileData,
                userPreferences,
                accessToken,
                mapToken,
                permissions,
                isLoading,
                isAuthenticated: user !== null && accessToken !== null,
                hydrated,
                setProfileData,
                setUserPreferences,
                signIn,
                signOut,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

// Alias for backward compatibility
export const UserProvider = AuthProvider;
export const useUser = useAuth;
